import { SegmentsBar } from '../../components/composition-bar/segments-bar.tsx'
import { SegmentsLabels } from '../../components/composition-bar/segments-labels.tsx'
import { TooltipWithHover } from '../../components/tooltip.tsx'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './security-posture-page.css.ts'

/**
 * A stacked bar under the figures it unlumps, with its breakdown on hover.
 *
 * The shell only — what the segments mean is the caller's business. Two of these sit on the estate card
 * (coverage-and-exposure, and uncontrolled vulnerabilities by exposure) and they were the same bar, the
 * same tooltip and the same caption with different rows; a second copy would have drifted.
 *
 * **A stacked bar is only honest for a partition.** Every caller must pass rows that each count a distinct
 * member of one whole — the summaries this page deliberately does *not* draw this way (actor trust levels,
 * control kinds) are overlapping sets, and stacking those implies a whole that does not exist.
 *
 * The breakdown is a hover tooltip rather than a legend beneath, the way the triage and SCA bars do it;
 * `SegmentsLabels` is the shared legend card, built to be the content of a `bare` tooltip, so this is the
 * same treatment rather than a lookalike.
 */

export type EstateBarRow = {
  key: string
  name: string
  count: number
  /** A resolved colour, so the tone vocabulary stays with the caller that owns the meaning. */
  color: string
}

type EstateCompositionBarProps = {
  rows: EstateBarRow[]
  /** The whole the rows partition. Segment widths are shares of this, not of their own sum. */
  total: number
  /** Sits under the bar, where a caption belongs — the bar is the thing, not the sentence about it. */
  note: string
  /** The tooltip card's heading. */
  heading: string
  /** Leads the accessible description, which then names every band and count. */
  ariaPrefix: string
  icon?: React.ReactNode
  /** Shown instead of the bar when there is no whole to divide. */
  emptyMessage: string
  /** What the counts are counting, for the tooltip's sub-labels. */
  unit?: { one: string; many: string }
}

const share = ({ count, total }: { count: number; total: number }): number => (total > 0 ? (count / total) * 100 : 0)

export const EstateCompositionBar: React.FC<EstateCompositionBarProps> = ({
  rows,
  total,
  note,
  heading,
  ariaPrefix,
  icon,
  emptyMessage,
  unit = { one: 'repository', many: 'repositories' },
}) => {
  if (total === 0) {
    return <p className={styles.exposureNote}>{emptyMessage}</p>
  }

  return (
    <div className={styles.exposureBlock}>
      <TooltipWithHover
        trigger={
          <SegmentsBar
            // `tiny`, matching the composition bars on the scan drilldown — those all default to it, and a
            // thicker bar here made the same control read as a different component.
            size="tiny"
            // The bar is the only thing carrying these numbers once the legend moves into a tooltip, so it
            // states them itself for anyone not hovering — or not using a pointer at all.
            role="img"
            aria-label={`${ariaPrefix} ${rows.map(row => `${row.name}: ${row.count} of ${total}`).join('. ')}`}
            segments={rows.map(row => ({
              ariaLabel: row.name,
              percentage: share({ count: row.count, total }),
              color: row.color,
            }))}
          />
        }
        maxWidth={1200}
        side="bottom"
        sideOffset={8}
        bare
      >
        <SegmentsLabels
          heading={heading}
          icon={icon}
          labels={rows.map(row => ({
            name: row.name,
            percentage: share({ count: row.count, total }),
            subLabel: `${row.count} ${pluralize(row.count, unit.one, unit.many)}`,
            color: row.color,
          }))}
        />
      </TooltipWithHover>
      {/* Under the bar: the bar is the figure, and this only says what it is measured over. */}
      <p className={styles.exposureNote}>{note}</p>
    </div>
  )
}
